import { Avatar, Box, Divider, Grid2 as Grid, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { getChatParticipant, postChat } from 'services/chat'
import Storage from 'storage/auth'
import { IUserSession } from 'types'
import { IPatientResponse } from 'types/patient/types'
import { getFullName } from 'utils/formatString'
import ChatInitialPage from '../Initial'
import SendMessageArea from './components/SendAreaSection'
import MessageList from './components/MessageList'
import { ChatParticipant } from 'helpers'
import { CHAT_PATIENT } from 'navigation/CONSTANTS'

const MessagePage: React.FC = () => {
  const { chatId } = useParams()
  const user = Storage.user.get()
  const [participants, setParticipants] = useState<
    Array<IPatientResponse | IUserSession>
  >([])
  const location = useLocation()
  const navigate = useNavigate()

  const chatCreationAttempted = useRef(false)

  const createChat = async (participantId: string) => {
    if (chatCreationAttempted.current) return
    chatCreationAttempted.current = true

    const response = await postChat(participantId)

    if (response.data) {
      const newChatId = response.data.chat_id
      navigate(`/${CHAT_PATIENT(newChatId)}`, {
        replace: true
      })
    } else {
      chatCreationAttempted.current = false
    }
  }

  useEffect(() => {
    const chat: ChatParticipant | undefined = location.state

    const fetch = async () => {
      const response = await getChatParticipant(chatId)

      if (response.success) {
        setParticipants(
          response.data?.filter(participant =>
            'professional_id' in participant
              ? participant.professional_id != user?.professional_id
              : participant.patient_id != user?.professional_id
          ) ?? []
        )
      }
    }

    const participant = chat?.participantList?.[0]

    if (chatId?.startsWith(':chatId')) {
      if (
        participant &&
        typeof participant === 'object' &&
        'participant_id' in participant &&
        participant.participant_id
      ) {
        createChat(participant.participant_id)
      }
    } else if (chat && chat.chat_id !== '') {
      setParticipants(
        new ChatParticipant(chatId ?? '', chat.participantList)
          .convertToInterfaceType
      )
    } else {
      fetch()
    }
  }, [chatId, location.state])

  const current = participants[0] ?? { first_name: '', last_name: '' }

  const fullName = getFullName(current)

  return (
    <Box
      sx={{
        height: '100%',
        position: 'relative'
      }}
    >
      <Grid container flexDirection='column' height='100%'>
        <Box
          sx={{
            paddingBottom: '8px',
            paddingTop: '4px',
            paddingX: '8px',
            display: 'flex',
            borderRadius: '0px',
            minHeight: '56px',
            backgroundColor: 'defaultComponent.main'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <Avatar
              src={current.user_image}
              alt={fullName}
              sx={{
                width: 48,
                height: 48,
                marginRight: '16px'
              }}
            />
            <Box
              textAlign='left'
              display={'flex'}
              flexDirection={'column'}
              sx={{
                width: 'calc(100% - 64px)'
              }}
            >
              <Typography
                variant='body1'
                noWrap
                overflow={'hidden'}
                textOverflow={'ellipsis'}
              >
                {fullName}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider />
        <MessageList chatId={chatId} userId={user?.professional_id} />
        <Divider />
        <SendMessageArea
          chatId={chatId}
          disabled={current.requested_deletion}
        />
      </Grid>
      <ChatInitialPage
        sx={{
          opacity: 0.18,
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: -1
        }}
      />
    </Box>
  )
}

export default MessagePage
